import type { RxCollection } from 'rxdb';
import type { AppDatabase } from './database';
import { defineStoredJson } from '../utils/storage';
import { dropFromOutbox, keepInOutbox } from './outbox';

/**
 * Who made each unsynced local change.
 *
 * The server attributes a change to whoever's token carried the push
 * (`routerFactory` hands `req.user.id` to the history). That is the right rule —
 * it cannot be forged — but it breaks when a device changes hands offline: the
 * outgoing user's queued edits would go up as the incoming user.
 *
 * So every local write is journalled against the user who made it. The push
 * splits its batch along that journal and sends each group with that user's own
 * kept token (see `utils/pendingCredentials`), leaving the server untouched.
 *
 * Only *local* writes are recorded: RxDB runs collection hooks from the document
 * API alone, never from the replication protocol, so documents arriving by pull
 * do not land here.
 */

/** collection name → document id → user id */
type Journal = Record<string, Record<string, string>>;

// Not a cache: this is who each unpushed change belongs to.
const journalValue = defineStoredJson<Journal>({
    key: 'pending_authors',
    fallback: () => ({}),
    keepOnWipe: true
});

let currentAuthorId: string | null = null;

/**
 * Whether the last attempt to write the journal actually landed.
 *
 * This file is the only record of what has not gone up yet: while the store
 * refuses, the journal is FROZEN. Entries can no longer be added and, worse, no
 * longer be removed, so the count stands still and every repair reports a
 * success it did not have. Whoever shows that count has to be able to say so.
 */
let writable = true;

function read(): Journal {
    return journalValue.read();
}

function write(journal: Journal): boolean {
    if (Object.keys(journal).length === 0) {
        journalValue.clear();
        writable = true;
        return true;
    }
    writable = journalValue.write(journal);
    return writable;
}

/**
 * False once the browser has refused to store the journal (full, or site data
 * blocked). The count cannot move again until this is true.
 */
export function isJournalWritable(): boolean {
    return writable;
}

/**
 * The user every subsequent local write belongs to. Set from `AuthProvider`
 * whenever the signed-in user changes; null while nobody is signed in.
 */
export function setCurrentAuthor(userId: string | null): void {
    currentAuthorId = userId;
}

/**
 * Who is signed in. The push needs this to tell "their own edits" (send with the
 * session token) from "a previous user's edits" (needs the token kept for them,
 * or the queue blocks).
 */
export function getCurrentAuthor(): string | null {
    return currentAuthorId;
}

/**
 * Credit one local write to the signed-in user. Called from the collection
 * hooks; exported so the journal can be exercised without a live RxDB.
 */
export function recordAuthor(collectionName: string, docId: string): void {
    if (!currentAuthorId || !docId) {
        return;
    }
    const journal = read();
    const forCollection = journal[collectionName] ?? {};
    if (forCollection[docId] === currentAuthorId) {
        return;
    }
    // Last writer wins: a document both users touched belongs to whoever
    // touched it last, which is also who the server would have credited.
    forCollection[docId] = currentAuthorId;
    journal[collectionName] = forCollection;
    write(journal);
}

export function authorsFor(
    collectionName: string,
    docIds: string[]
): Map<string, string> {
    const forCollection = read()[collectionName] ?? {};
    const authors = new Map<string, string>();
    for (const id of docIds) {
        const author = forCollection[id];
        if (author) {
            authors.set(id, author);
        }
    }
    return authors;
}

/**
 * Forget documents that have been accepted upstream.
 *
 * `seen` is what the push read out of the journal before it sent anything, and
 * an entry is only dropped when it still says the same thing. Between the read
 * and the answer from the server the document may have been written AGAIN —
 * possibly by the next person on a shared tablet — and that second change is
 * still waiting: clearing it would file it under whoever pushes next. Given no
 * `seen`, the ids are dropped outright (see `forgetPending`).
 */
export function clearAuthors(
    collectionName: string,
    docIds: string[],
    seen?: Map<string, string>
): void {
    if (docIds.length === 0) {
        return;
    }
    const journal = read();
    const forCollection = journal[collectionName];
    if (!forCollection) {
        return;
    }
    const cleared: string[] = [];
    for (const id of docIds) {
        if (seen && forCollection[id] !== seen.get(id)) {
            continue;
        }
        delete forCollection[id];
        cleared.push(id);
    }
    if (Object.keys(forCollection).length === 0) {
        delete journal[collectionName];
    } else {
        journal[collectionName] = forCollection;
    }
    write(journal);
    // Accepted upstream means the server has it, so the second copy has done its
    // job (db/outbox). Only the ids that actually cleared above: a document
    // written again while its push was in flight is still outstanding, and its
    // copy has to stay for the same reason its journal entry does.
    void dropFromOutbox(collectionName, cleared);
}

/** One document still waiting to go up, and who wrote it. */
export interface PendingEntry {
    collection: string;
    docId: string;
    userId: string;
}

/**
 * Everything the journal is holding, one entry per document. The count alone
 * ("2 ausstehend") is not something anybody can act on once it stops going down
 * — see `features/sync/pendingQueue`, which pairs each entry with the document
 * it names and can then say whether it is still real.
 */
export function pendingEntries(): PendingEntry[] {
    const entries: PendingEntry[] = [];
    for (const [collection, forCollection] of Object.entries(read())) {
        for (const [docId, userId] of Object.entries(forCollection)) {
            entries.push({ collection, docId, userId });
        }
    }
    return entries;
}

/**
 * Drop entries outright, without a push having accepted them.
 *
 * The escape hatch for an entry that names a document nothing will ever send —
 * the local database was rebuilt under it, or the server has long had that state
 * (see `features/sync/pendingQueue` for what has to be proven first). Everything
 * else clears itself through `clearAuthors` on the way up.
 */
export function forgetPending(
    entries: readonly { collection: string; docId: string }[]
): boolean {
    const byCollection = new Map<string, string[]>();
    for (const entry of entries) {
        const ids = byCollection.get(entry.collection);
        if (ids) {
            ids.push(entry.docId);
        } else {
            byCollection.set(entry.collection, [entry.docId]);
        }
    }
    for (const [collection, ids] of byCollection) {
        clearAuthors(collection, ids);
    }
    // A refused write leaves everything exactly where it was; saying "removed"
    // over a frozen journal is the one answer nobody can act on.
    return isJournalWritable();
}

export function pendingCountsByAuthor(): Map<string, number> {
    const counts = new Map<string, number>();
    for (const forCollection of Object.values(read())) {
        for (const author of Object.values(forCollection)) {
            counts.set(author, (counts.get(author) ?? 0) + 1);
        }
    }
    return counts;
}

/**
 * How many local changes are still waiting to go up, from this browser. The
 * journal holds a document from the moment it is written until the push is
 * accepted, it lives in a store every tab shares, and it survives a reload — a
 * counter in one tab's memory counts only what THAT tab wrote, since it opened.
 */
export function pendingLocalChanges(): number {
    let total = 0;
    for (const forCollection of Object.values(read())) {
        total += Object.keys(forCollection).length;
    }
    return total;
}

/** Hear about the journal changing — in this tab or in another one. */
export const subscribeAuthorJournal = journalValue.subscribe;

export function hasPendingChanges(userId: string): boolean {
    return (pendingCountsByAuthor().get(userId) ?? 0) > 0;
}

/**
 * Journal every local write across every collection. Called once, right after
 * the collections are created, so no write can slip past it.
 */
export function installAuthorJournal(db: AppDatabase): void {
    for (const [name, collection] of Object.entries(db.collections)) {
        const typed = collection as RxCollection;
        const primaryPath = typed.schema.primaryPath as string;
        /**
         * Journal the write and keep a copy of what it WILL be.
         *
         * The condition is `recordAuthor`'s own: a copy without a journal entry
         * beside it is unreachable — nothing looks for it and nothing ever
         * deletes it — so it would sit in IndexedDB for the life of the install.
         */
        const journalWrite = (
            data: Record<string, unknown>,
            stored: Record<string, unknown> = data
        ) => {
            const docId = String(data[primaryPath] ?? '');
            if (!currentAuthorId || !docId) {
                return;
            }
            recordAuthor(name, docId);
            // The same moment, the whole document: the journal says which
            // documents are outstanding, the outbox says what is in them, and
            // only the pair survives a database that will not open (db/outbox).
            void keepInOutbox(name, docId, stored, typed.schema.version);
        };
        /**
         * BEFORE the write, not after: several windows share one database, and
         * the one that replicates sees a document the moment it lands in
         * storage. With a `post` hook it could push and clear that document
         * while the writing window had not journalled it yet — and the entry
         * then written would name a document nobody will ever send again.
         *
         * The cost is the other way round: a write that is REJECTED (validation)
         * leaves an entry behind. Those are swept up by the self-check in
         * `features/sync/pendingQueue`. A removal is a soft delete here and
         * still has to be pushed, so it is journalled too.
         */
        typed.preInsert((data) => journalWrite(data), false);
        typed.preSave((data) => journalWrite(data), false);
        /**
         * A removal needs its own: RxDB runs this hook on the document as it
         * still stands and sets `_deleted` only afterwards, on a clone it makes
         * for the write (`bulkRemove` in rx-collection). Copied as handed over,
         * the outbox would hold a LIVE document — and a rescue would then put a
         * Befund somebody deleted in the field back on the server.
         */
        typed.preRemove(
            (data) => journalWrite(data, { ...data, _deleted: true }),
            false
        );
    }
}
